import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportRevenue } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

const payColors: Record<string, string> = {
  cash: '#22c55e',
  card: '#6366f1',
  storeCredit: '#f59e0b',
  other: '#94a3b8',
};

export function RevenueReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-revenue', qBuilt],
    queryFn: () => fetchReportRevenue(qBuilt),
  });

  const d = q.data;

  const pieData = useMemo(() => {
    const bd = d?.summary.paymentBreakdown;
    if (!bd) return [];
    return [
      { name: 'Cash', key: 'cash', value: Number(bd.cash) },
      { name: 'Card', key: 'card', value: Number(bd.card) },
      { name: 'Store credit', key: 'storeCredit', value: Number(bd.storeCredit) },
      { name: 'Other', key: 'other', value: Number(bd.other) },
    ].filter((x) => x.value > 0);
  }, [d]);

  const chartRows = useMemo(() => {
    const rows = d?.chart ?? [];
    return rows.map((r) => ({
      ...r,
      dLabel: r.date.length > 10 ? r.date.slice(0, 10) : r.date,
      revenueNum: Number(r.revenue),
    }));
  }, [d]);

  const csv = () => {
    if (!d) return;
    exportToCsv(
      (d.chart ?? []).map((r) => ({
        date: r.date,
        revenue: r.revenue,
        tickets: r.tickets,
        avgTicketValue: r.avgValue,
      })),
      'revenue-series.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading revenue…</div>;
  if (q.isError || !d) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load revenue report.</div>;

  const s = d.summary;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          { label: 'Total revenue', v: formatCurrency(s.totalRevenue) },
          { label: 'Tickets closed', v: String(s.totalTickets) },
          { label: 'Avg ticket', v: formatCurrency(s.avgTicketValue) },
          { label: 'Tax', v: formatCurrency(s.totalTax) },
          { label: 'Discounts', v: formatCurrency(s.totalDiscounts) },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{k.label}</p>
            <p className="mt-1 text-xl font-bold text-slate-950">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Payment mix</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                  {pieData.map((e) => (
                    <Cell key={e.key} fill={payColors[e.key as keyof typeof payColors] ?? '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {(d.byStore ?? []).length > 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-950">Revenue by store</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={d.byStore.map((row) => ({ ...row, revenueNum: Number(row.revenue) }))}
                  layout="vertical"
                  margin={{ left: 16 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="storeName" width={112} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Bar dataKey="revenueNum" fill="#0ea5e9" radius={[0, 4, 4, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500 shadow-sm">
            Store comparison appears when viewing all stores as an admin with multiple locations.
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-950">Revenue over time</h3>
        <div className="mt-4 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="dLabel" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Line type="monotone" dataKey="revenueNum" name="Revenue" stroke="#6366f1" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3">Period</th>
              <th className="px-4 py-3">Revenue</th>
              <th className="px-4 py-3">Tickets</th>
              <th className="px-4 py-3">Avg value</th>
            </tr>
          </thead>
          <tbody>
            {chartRows.map((r) => (
              <tr key={r.date} className="border-b border-slate-100">
                <td className="px-4 py-2 font-mono text-xs">{r.date}</td>
                <td className="px-4 py-2">{formatCurrency(r.revenue)}</td>
                <td className="px-4 py-2">{r.tickets}</td>
                <td className="px-4 py-2">{formatCurrency(r.avgValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
